import AddOnIds from '../constants/addOnIds';

/** Prefix shared by geo `CTCT-SMS-ADDON` and market-specific response ids such as `CTCT-SMS-ADDON-UK`. */
export const SMS_MARKET_ADDON_ID_PREFIX = 'CTCT-SMS-ADDON';

export const isSmsMarketAddOnId = (addOnId?: string): boolean =>
  Boolean(addOnId?.startsWith(SMS_MARKET_ADDON_ID_PREFIX));

interface AddOnWithId {
  id?: string;
}

interface RatedAddOn extends AddOnWithId {
  totalPrice?: number;
  monthlyCost?: number;
  proRatedTotal?: number;
}

/** Rating entries with zero amounts are placeholders; prefer market-specific addons with real prices. */
export const hasSmsRatingAmounts = (addOn: RatedAddOn): boolean =>
  [addOn.totalPrice, addOn.monthlyCost, addOn.proRatedTotal].some((amount) => amount != null && amount > 0);

/**
 * Resolves the SMS add-on from a catalog or rating list.
 * Primary: match `CTCT-SMS-ADDON` (canonical id for requests and geo responses).
 * Fallback: first other `CTCT-SMS-ADDON*` entry that passes `isUsable` when the backend
 * returns a market-specific response id (e.g. `CTCT-SMS-ADDON-AUSTRALIA`).
 */
export const findSmsAddOnInList = <T extends AddOnWithId>(
  addOns: T[] | null | undefined,
  preferredSmsAddOnId: string = AddOnIds.CTCT_SMS_ADDON,
  isUsable: (addOn: T) => boolean = () => true
): T | undefined => {
  if (!addOns?.length) {
    return undefined;
  }

  const preferredAddOn = addOns.find((addOn) => addOn.id === preferredSmsAddOnId);

  if (preferredAddOn && isUsable(preferredAddOn)) {
    return preferredAddOn;
  }

  return addOns.find((addOn) => addOn.id && isSmsMarketAddOnId(addOn.id) && isUsable(addOn));
};

export const findSmsAddOnRating = <T extends RatedAddOn>(
  addOnRating: T[] | null | undefined,
  preferredSmsAddOnId?: string
): T | undefined => findSmsAddOnInList(addOnRating, preferredSmsAddOnId, hasSmsRatingAmounts);
